import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { ArrowLeft, Mic } from 'lucide-react';

const KaraokeLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await signIn(email, password);
    // Note: FirebaseAuthContext will handle the redirect automatically
    // based on the user's account_type, so we don't need to navigate here
    setLoading(false);
  };

  return (
    <>
      <Helmet>
        <title>Karaoke Mode Login - Your Song Request</title>
        <meta name="description" content="Login to your karaoke host account and manage your karaoke sessions." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-purple-950 via-purple-900 to-indigo-950 flex items-center justify-center px-4">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/10 via-indigo-900/10 to-purple-900/10"></div>
        
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 w-full max-w-md"
        >
          <Link to="/karaoke" className="inline-flex items-center text-purple-300 hover:text-purple-200 mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Karaoke Mode
          </Link>

          <Card className="glass-effect border-purple-700 bg-purple-900/20">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <Mic className="w-12 h-12 text-purple-400" />
              </div>
              <CardTitle className="text-2xl font-bold text-purple-100">🎤 Karaoke Mode Login</CardTitle>
              <CardDescription className="text-purple-300">
                Access your karaoke host dashboard
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-purple-200">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-purple-900/50 border-purple-600 text-white placeholder-purple-400"
                    placeholder="your@email.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-purple-200">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-purple-900/50 border-purple-600 text-white placeholder-purple-400"
                    placeholder="••••••••"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2"
                >
                  {loading ? 'Logging in...' : 'Login to Karaoke Mode'}
                </Button>
              </form>
              
              {/* Artist Mode Link */}
              <div className="mt-6 p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
                <p className="text-blue-200 text-sm mb-2">
                  🎵 Looking for Artist Mode?
                </p>
                <Link 
                  to="/login" 
                  className="inline-flex items-center text-blue-300 hover:text-blue-200 font-semibold text-sm transition-colors"
                >
                  Login HERE! 🎵
                </Link>
                <p className="text-blue-300 text-xs mt-1">
                  Perfect for Musicians and Bands
                </p>
              </div>

              {/* Sign Up Link */}
              <div className="mt-4 text-center">
                <p className="text-purple-300 text-sm">
                  Don't have a karaoke account?{' '}
                  <Link to="/karaoke" className="text-purple-200 hover:text-purple-100 font-semibold">
                    Sign up here
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </>
  );
};

export default KaraokeLoginPage;
